import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.users_api.users;


// Helper function to configure the Axios instance with auth interceptor
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'users_api',
  group: 'users',
  defaultTtl: '5m',
  hashLargeSegments: true,
  emitMeta: true,
});


// Function to fetch a user by ID
export const getUserById = async (userId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUserById/${userId}`, undefined, { ttl: '30m' })
  ) as any;
};

// Function to fetch a user by email
export const getUserByEmail = async (email: string) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUserByEmail/${email}`)
  ) as any;
};

// Function to create a user
export const createUser = async (userData: any) => {
  const res = await retryWithBackoff(() => 
    api.post('/CreateUser', userData)
  ) as any;
  await cachedApi.clear();
  return res;
};

// Function to update a user by ID
export const updateUserById = async (userId: number, userData: any) => {
  const res = await retryWithBackoff(() => 
    api.put(`/UpdateUserById/${userId}`, userData)
  ) as any;
  await cachedApi.clear();
  return res;
};

// Function to delete a user by ID
export const deleteUserById = async (userId: number) => {
  const res = await retryWithBackoff(() => 
    api.delete(`/DeleteUserById/${userId}`)
  ) as any;
  await cachedApi.clear();
  return res;
};

// Additional fetch functions for other endpoints
export const getUsersByLocalId = async (localId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUsersByLocalId/${localId}`)
  ) as any;
};

// /GetUserMapByLocalId/<int:local_id>

export const getUserMapByLocalId = async (localId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUserMapByLocalId/${localId}`)
  ) as any;
};

export const getUsersByCompanyId = async (companyId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUsersByCompanyId/${companyId}`)
  ) as any;
};

export const getUsersByCityId = async (cityId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUsersByCityId/${cityId}`)
  ) as any;
};

export const getUsersByStateId = async (stateId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUsersByStateId/${stateId}`)
  ) as any;
};

export const getUsersByCountryId = async (countryId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUsersByCountryId/${countryId}`)
  ) as any;
};

export const getUsersByRoleId = async (roleId: number) => {
  return await retryWithBackoff(() => 
    cachedApi.get(`/GetUsersByRoleId/${roleId}`)
  ) as any;
};

// /ValidateUserEmail/<int:user_id>

export const validateUserEmail = async (userId:number) => {
  const res = await retryWithBackoff(() => 
    api.put(`/ValidateUserEmail/${userId}`)
  ) as any;
  await cachedApi.clear();
  return res;
}

// /SendResetPasswordEmail/<string:email>

export const sendResetPasswordEmail = async (email:string) => {
  // Avoid caching this side-effect GET, but add retry
  return await retryWithBackoff(() => 
    api.get(`/SendResetPasswordEmail/${email}`)
  ) as any;
}

export const resetUserPassword = async (user_id: number, code: string, password: string) => {
  const res = await retryWithBackoff(() => 
    api.put(`/ResetUserPassword/${user_id}`, { code, password })
  ) as any;
  await cachedApi.clear();
  return res;
}

export const CreateUserRegistration = async (userData: any) => {
  const res = await retryWithBackoff(() => 
    api.post('/CreateUserRegistration', userData)
  ) as any;
  await cachedApi.clear();
  return res;
};

export const DeleteUserRegistration = async (code: string) => {
  const res = await retryWithBackoff(() => 
    api.delete(`/DeleteUserRegistration/${code}`)
  ) as any;
  await cachedApi.clear();
  return res;
}

export const deactivateSelectedUsers = async (usersToDeactivate: string[]) => {
  try {
    for (const userId of usersToDeactivate) {
      await retryWithBackoff(() => 
        api.put('/DeactivateUsers', {
          users_to_deactivate: userId, // Enviar un solo ID en cada solicitud
        })
      ) as any;
    }
  } catch (error) {
    console.error('Error al desactivar usuarios:', error);
    throw error;
  }
  // Clear the users scope after batch operation completes
  await cachedApi.clear();
};